import { useMemo } from "react";
import { useOpportunities, type OpportunityWithRelations } from "@/hooks/use-opportunities";
import { useOffers, type OfferWithCustomer } from "@/hooks/use-offers-queries";
import { useOfferPipelineWeights, FALLBACK_WEIGHTS } from "@/hooks/use-offer-pipeline";
import { useFollowUpRuleThresholds } from "@/hooks/use-crm-config";
import { buildPipeBoard, type PipeBoard } from "@/lib/pipe-board-calc";
import type { OpportunitySource } from "@/lib/db-types";

export interface PipeBoardFilter {
  /** Utelatt = alle rådgivere. */
  ownerId?: string;
  source?: OpportunitySource;
  /** False til filteret er klart (f.eks. «Mine» før brukeren er lastet). */
  enabled?: boolean;
}

export interface UsePipeBoard {
  board: PipeBoard<OpportunityWithRelations, OfferWithCustomer>;
  isLoading: boolean;
}

/**
 * Pipe-brettet: åpne prosesser i Outreach, Møte og Tilbud, med tilbudene som
 * hører til dem (useOffers filtrert på prosessenes offer_id), vektene fra
 * system_config og stillstandsterskelen fra oppfølgingsreglene. Brettet er
 * en avledning av andre spørringer; invalidering skjer via ["opportunities"]
 * og ["offers"] (CACHE_REGISTRY).
 */
export function usePipeBoard(filter: PipeBoardFilter = {}): UsePipeBoard {
  const { data: opportunities = [], isLoading: oppLoading } = useOpportunities({
    ownerId: filter.ownerId,
    source: filter.source,
    enabled: filter.enabled,
  });
  const { data: offers = [], isLoading: offersLoading } = useOffers();
  const { data: weights = FALLBACK_WEIGHTS } = useOfferPipelineWeights();
  const { data: thresholds } = useFollowUpRuleThresholds();
  const outreachDays = thresholds?.outreach_no_reply_days ?? 10;

  const board = useMemo(
    () =>
      buildPipeBoard({
        opportunities,
        offers,
        weights,
        thresholds: { outreach_no_reply_days: outreachDays },
        now: new Date(),
      }),
    [opportunities, offers, weights, outreachDays],
  );

  return { board, isLoading: oppLoading || offersLoading };
}
